<template>
  <!-- 1) Use your existing iPhone frame as a wrapper -->
  <IPhoneSkeleton :dir="isRtl ? 'rtl' : 'ltr'">
    <div class="whatsapp">
      <!-- === HEADER === -->
      <div class="whatsapp-header">
        <div class="left-accessory d-flex">
          <div class="go-back d-flex">
            <v-icon
              size="22"
              color="#007aff"
            >
              {{ isRtl ? icons.mdiChevronRight : icons.mdiChevronLeft }}
            </v-icon>
            <span class="header-counter">14</span>
          </div>
          <div class="profile d-flex">
            <div>
              <v-img
                width="28"
                :src="require('@/assets/images/whatsapp/whatsapp-profile.svg')"
              />
            </div>
            <div class="profile-title d-flex">
              <div class="profile-name">
                Anita Lee
              </div>
              <div class="online-mark">
                {{ onlineText }}
              </div>
            </div>
          </div>
        </div>
        <div class="right-accessory">
          <div class="call-icons">
            <!-- Optional icons for voice/video calls -->
          </div>
        </div>
      </div>
      <!-- === END HEADER === -->

      <!-- === BODY === -->
      <div class="whatsapp-body">
        <!-- Date indicator (if you'd like to display something like 'Today', 'Yesterday', etc.) -->
        <div
          v-if="dateIndicator"
          class="date-indicator"
        >
          <div class="date-btn">
            {{ dateIndicator }}
          </div>
        </div>

        <!-- Loop through all messages passed by props -->
        <div
          v-for="(msg, index) in messages"
          v-show="msg.content"
          :key="index"
          :class="['message-block', { 'rtl-message': isRtl }]"
        >
          <!-- Media + Content -->
          <div class="card-content">
            <!-- Optional image -->
            <div v-if="msg.imageUrl">
              <v-img
                class="card-media"
                :src="msg.imageUrl"
                contain
              />
            </div>

            <!-- Text content -->
            <div
              class="message-text"
              v-text="msg.content"
            >
            </div>
          </div>

          <!-- Time -->
          <div class="message-block-date">
            {{ msg.time || defaultTime }}
          </div>

          <!-- Buttons (if any) -->
          <div
            v-if="msg.buttons && msg.buttons.length"
            class="card-buttons"
          >
            <div
              v-for="(btn, btnIndex) in msg.buttons"
              :key="btnIndex"
              class="card-button"
              :class="{ last: btnIndex === msg.buttons.length - 1 }"
            >
              <!-- Icon -->
              <v-icon
                size="8"
                color="#007aff"
              >
                {{ isRtl ? btn.rtlIcon : btn.icon }}
              </v-icon>

              <!-- Button Text -->
              {{ btn.text }}
            </div>
          </div>
        </div>
        <!-- END messages loop -->
      </div>
      <!-- === END BODY === -->

      <!-- === FOOTER === -->
      <div class="whatsapp-footer">
        <!-- Optionally place a text input or more icons here -->
      </div>
      <!-- === END FOOTER === -->
    </div>
  </IPhoneSkeleton>
</template>

<script>
import { mdiChevronLeft, mdiChevronRight, mdiSubdirectoryArrowLeft, mdiSubdirectoryArrowRight } from '@mdi/js'

import { i18n } from '@/plugins/i18n/index'
import { ref, watch } from '@vue/composition-api'
import IPhoneSkeleton from './IPhoneSkeleton.vue'
// eslint-disable-next-line import/no-cycle

export default {
  name: 'WhatsAppSkeleton',
  components: {
    IPhoneSkeleton,
  },
  props: {
    /**
     * Whether the layout should be right-to-left.
     */
    isRtl: {
      type: Boolean,
      default: false,
    },

    lang: {
      type: String,
      required: true,
    },

    /**
     * The array of messages to display.
     * Each message can be shaped like:
     * {
     *   content: "string",
     *   imageUrl?: "string", // optional
     *   time?: "string",
     *   buttons?: [
     *     { text: "I'm Coming", icon: mdiSubdirectoryArrowLeft, rtlIcon: mdiSubdirectoryArrowRight },
     *     ...
     *   ]
     * }
     */
    messages: {
      type: Array,
      default: () => [],
    },

    /**
     * If a message doesn't include its own time,
     * we can fall back to a default (e.g., current time).
     */
    defaultTime: {
      type: String,
      default: '',
    },
  },
  setup(props) {
    // If you want a dynamic fallback time, you can do:
    const getCurrentTime = () => {
      const now = new Date()
      const hours = String(now.getHours()).padStart(2, '0')
      const minutes = String(now.getMinutes()).padStart(2, '0')

      return `${hours}:${minutes}`
    }

    const dateIndicator = ref(i18n.t('Today', props.lang))
    const onlineText = ref(i18n.t('whatsappOnline', props.lang))

    // Example: if "defaultTime" prop is not provided, generate once
    const fallbackTime = ref(props.defaultTime || getCurrentTime())

    // Consolidate icons so you can reference them easily
    const icons = {
      mdiChevronLeft,
      mdiChevronRight,
      mdiSubdirectoryArrowLeft,
      mdiSubdirectoryArrowRight,
    }

    watch(
      () => props.lang,
      newLang => {
        dateIndicator.value = i18n.t('Today', newLang)
        onlineText.value = i18n.t('whatsappOnline', newLang)
      },
    )

    return {
      icons,
      fallbackTime,
      dateIndicator,
      onlineText,
    }
  },
}
</script>

<style lang="scss" scoped>
.whatsapp {
  margin-top: 30px;
  position: absolute;
  height: 100%;
  width: 100%;
  background-image: url('~@/assets/images/whatsapp/background.png') !important;
  background-repeat: no-repeat;
  background-size: cover;
}

.whatsapp-header {
  width: 100%;
  height: 30px;
  background: #f6f6f6;
  padding-inline: 6px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

/* Left Accessory (Back button, profile) */
.left-accessory {
  align-items: center;
  display: flex;
}

.go-back {
  color: #007aff;
  align-items: center;
  display: flex;
}

.header-counter {
  font-size: 10px;
  margin-inline-start: -5px;
}

.profile {
  display: flex;
  align-items: center;
  padding-inline-start: 10px;

  .profile-title {
    display: flex;
    flex-direction: column;
    padding-inline-start: 4px;

    .profile-name {
      font-size: 10px;
      color: #000000;
      line-height: 12px;
      font-weight: 500;
    }
    .online-mark {
      color: #898989;
      font-size: 7px;
    }
  }
}

/* Body scrolling area */
.whatsapp-body {
  max-height: 340px;
  overflow: auto;
  scroll-behavior: smooth;
  scrollbar-color: transparent transparent;
}

/* Date indicator (like "Today") */
.date-indicator {
  width: 100%;
  display: flex;
  justify-content: center;
  padding-top: 10px;

  .date-btn {
    background: #dbdfeb;
    width: 70px;
    height: 12px;
    border-radius: 4px;
    color: #414350;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 8px;
    font-weight: 700;
  }
}

/* Single message bubble */
.message-block {
  position: relative;
  width: 80%;
  background: #f9f9f9;
  border-radius: 5px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  margin: 10px 0;
  margin-inline-start: 8px;
  font-size: 10px;
  color: #000000;

  &::after {
    content: '';
    position: absolute;
    bottom: 3px;
    left: -6px; /* pointer on the left for LTR */
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 3px 7px 7px 0;
    border-color: transparent #f9f9f9 transparent transparent;
  }
}

/* Flip pointer for RTL */
.rtl-message {
  &::after {
    left: auto;
    right: -6px;
    border-width: 3px 0 7px 7px;
    border-color: transparent transparent transparent #f9f9f9;
  }
}

/* Message content area */
.card-content {
  padding: 5px;
  padding-bottom: 0;

  .card-media {
    margin-bottom: 5px;
    max-width: 300px;
    max-height: 300px;
    border-radius: 8px;
    overflow: hidden;
  }

  .message-text {
    /* multiline text, if needed: */
    white-space: pre-wrap;
  }
}

/* Time at the bottom-right (or bottom-left if we use text-align) */
.message-block-date {
  text-align: end;
  color: rgba(0, 0, 0, 0.2);
  font-weight: 600;
  font-size: 8px;
  line-height: 8px;
  padding-bottom: 5px;
  padding-inline-end: 5px;
}

/* Buttons container */
.card-buttons {
  display: flex;
  flex-direction: column;
  margin-top: 5px;
}

/* Single button */
.card-button {
  background: none;
  border: none;
  border-top: 0.5px solid #e0e0e0; /* thin gray border on top */
  padding: 5px 0;
  font-size: 10px;
  color: #278257;
  cursor: pointer;
  text-align: center;

  /* Icon and text side by side */
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 5px;
}
</style>
